import type { EnglishTranslations } from './en';
import type { TranslationShape } from './types';

export const ar = {
  app: {
    name: 'سطر PDF',
    shortName: 'سطر PDF',
    tagline: 'أدوات PDF بخصوصية تامة على جهازك.',
    heroTitle: 'أدوات PDF تبقى على جهازك',
    heroDescription: 'حرّر ملفات PDF وحوّلها واحمِها وافهم محتواها بخصوصية، حتى دون اتصال.',
    privacyPromise: 'ملفاتك لا تغادر هذا الجهاز.',
    localProcessing: 'تجري كل المعالجة محليًا داخل متصفحك.',
    loading: 'جارٍ تحميل سطر PDF…',
  },
  common: {
    actions: {
      add: 'إضافة',
      addFiles: 'إضافة ملفات',
      addMoreFiles: 'إضافة مزيد من الملفات',
      apply: 'تطبيق',
      back: 'رجوع',
      cancel: 'إلغاء',
      chooseFile: 'اختيار ملف',
      chooseFiles: 'اختيار ملفات',
      clear: 'مسح',
      close: 'إغلاق',
      confirm: 'تأكيد',
      continue: 'متابعة',
      copy: 'نسخ',
      delete: 'حذف',
      download: 'تنزيل',
      downloadAll: 'تنزيل الكل',
      downloadZip: 'تنزيل الكل كملف ZIP',
      edit: 'تحرير',
      install: 'تثبيت',
      learnMore: 'معرفة المزيد',
      moveDown: 'نقل إلى أسفل',
      moveEarlier: 'نقل إلى موضع سابق',
      moveLater: 'نقل إلى موضع لاحق',
      moveUp: 'نقل إلى أعلى',
      next: 'التالي',
      open: 'فتح',
      pause: 'إيقاف مؤقت',
      preview: 'معاينة',
      previous: 'السابق',
      process: 'المعالجة على هذا الجهاز',
      redo: 'إعادة',
      remove: 'إزالة',
      rename: 'إعادة تسمية',
      reset: 'إعادة الضبط',
      resume: 'استئناف',
      retry: 'المحاولة مجددًا',
      save: 'حفظ',
      search: 'بحث',
      selectAll: 'تحديد الكل',
      selectNone: 'إلغاء تحديد الكل',
      settings: 'الإعدادات',
      startOver: 'بدء من جديد',
      startOverAndClear: 'البدء من جديد ومسح البيانات المؤقتة',
      undo: 'تراجع',
      update: 'تحديث',
    },
    status: {
      available: 'متاح',
      canceled: 'أُلغيت',
      canceling: 'جارٍ الإلغاء…',
      complete: 'مكتمل',
      completedLocally: 'اكتملت المعالجة على هذا الجهاز',
      failed: 'فشلت',
      installed: 'مثبّت',
      installing: 'جارٍ التثبيت…',
      loading: 'جارٍ التحميل…',
      notInstalled: 'غير مثبّت',
      paused: 'متوقفة مؤقتًا',
      preparing: 'جارٍ التحضير…',
      processing: 'جارٍ المعالجة…',
      queued: 'في قائمة الانتظار',
      ready: 'جاهز',
      unavailable: 'غير متاح',
      validating: 'جارٍ التحقق من الناتج…',
    },
    labels: {
      all: 'الكل',
      automatic: 'تلقائي',
      color: 'اللون',
      currentPage: 'الصفحة الحالية',
      file: 'الملف',
      files: 'الملفات',
      fileName: 'اسم الملف',
      fileOrder: 'ترتيب الملفات',
      fileSize: 'حجم الملف',
      format: 'التنسيق',
      from: 'من',
      height: 'الارتفاع',
      language: 'اللغة',
      margins: 'الهوامش',
      opacity: 'العتامة',
      options: 'الخيارات',
      output: 'الناتج',
      outputFilename: 'اسم ملف الناتج',
      page: 'الصفحة',
      pages: 'الصفحات',
      pageCount: 'عدد الصفحات',
      pageOrder: 'ترتيب الصفحات',
      pageRange: 'نطاق الصفحات',
      pageSize: 'حجم الصفحة',
      password: 'كلمة المرور',
      position: 'الموضع',
      quality: 'الجودة',
      resolution: 'الدقة',
      rotation: 'زاوية الدوران',
      scale: 'المقياس',
      selectedPages: 'الصفحات المحددة',
      size: 'الحجم',
      to: 'إلى',
      type: 'النوع',
      units: 'الوحدة',
      width: 'العرض',
    },
    choices: {
      allPages: 'كل الصفحات',
      selectedPages: 'الصفحات المحددة',
      oddPages: 'الصفحات الفردية',
      evenPages: 'الصفحات الزوجية',
      portrait: 'عمودي',
      landscape: 'أفقي',
      yes: 'نعم',
      no: 'لا',
      on: 'مفعّل',
      off: 'متوقف',
    },
    counts: {
      files_zero: 'لا ملفات',
      files_one: 'ملف واحد',
      files_two: 'ملفان',
      files_few: '{{count}} ملفات',
      files_many: '{{count}} ملفًا',
      files_other: '{{count}} ملف',
      pages_zero: 'لا صفحات',
      pages_one: 'صفحة واحدة',
      pages_two: 'صفحتان',
      pages_few: '{{count}} صفحات',
      pages_many: '{{count}} صفحة',
      pages_other: '{{count}} صفحة',
      results_zero: 'لا نتائج',
      results_one: 'نتيجة واحدة',
      results_two: 'نتيجتان',
      results_few: '{{count}} نتائج',
      results_many: '{{count}} نتيجة',
      results_other: '{{count}} نتيجة',
      differences_zero: 'لا اختلافات',
      differences_one: 'اختلاف واحد',
      differences_two: 'اختلافان',
      differences_few: '{{count}} اختلافات',
      differences_many: '{{count}} اختلافًا',
      differences_other: '{{count}} اختلاف',
    },
    units: {
      byte: 'بايت',
      kilobyte: 'كيلوبايت',
      megabyte: 'ميغابايت',
      gigabyte: 'غيغابايت',
      percent: '{{value}}٪',
      degrees: '{{value}}°',
      dpi: '{{value}} DPI',
      millimeters: '{{value}} مم',
      points: '{{value}} نقطة',
    },
  },
  language: {
    names: {
      english: 'English',
      arabic: 'العربية',
    },
    firstRun: {
      headingEnglish: 'Choose your language',
      headingArabic: 'اختر لغتك',
      descriptionEnglish: 'You can change this later in Settings.',
      descriptionArabic: 'يمكنك تغيير اللغة لاحقًا من الإعدادات.',
      privacyEnglish: 'Files stay on this device.',
      privacyArabic: 'تبقى الملفات على هذا الجهاز.',
      useEnglish: 'Use English',
      useArabic: 'استخدام العربية',
    },
    switcherLabel: 'تغيير اللغة',
    currentLanguage: 'اللغة الحالية: {{language}}',
    changedTo: 'تغيّرت اللغة إلى {{language}}.',
  },
  navigation: {
    home: 'الرئيسية',
    allTools: 'كل الأدوات',
    favorites: 'المفضلة',
    recent: 'المستخدمة مؤخرًا',
    offline: 'دون اتصال',
    settings: 'الإعدادات',
    about: 'حول التطبيق',
    privacy: 'الخصوصية',
    skipToContent: 'التخطي إلى المحتوى الرئيسي',
    openMenu: 'فتح قائمة التنقل',
    closeMenu: 'إغلاق قائمة التنقل',
    breadcrumbs: 'مسار التنقل',
  },
  home: {
    searchLabel: 'البحث في أدوات PDF',
    searchPlaceholder: 'ابحث في ٣٣ أداة PDF',
    searchHint: 'ابحث حسب المهمة أو التنسيق أو اسم الأداة.',
    noSearchResults: 'لا توجد أدوات تطابق «{{query}}».',
    clearSearch: 'مسح البحث',
    categoriesTitle: 'كل أدوات PDF',
    categoriesDescription: 'اختر أداة. يبقى مستندك على هذا الجهاز من البداية إلى النهاية.',
    favoritesTitle: 'الأدوات المفضلة',
    favoritesEmpty: 'أضف الأدوات إلى المفضلة لتبقى هنا.',
    recentTitle: 'الأدوات المستخدمة مؤخرًا',
    recentEmpty: 'ستظهر الأدوات التي تستخدمها هنا. لا تُحفظ أسماء الملفات أو تفاصيل المستندات.',
    addFavorite: 'إضافة {{tool}} إلى المفضلة',
    removeFavorite: 'إزالة {{tool}} من المفضلة',
    privacyTitle: 'الخصوصية جزء من التصميم',
    privacyDescription: 'لا رفع للملفات ولا حسابات ولا تحليلات ولا خوادم لمعالجة المستندات.',
    offlineTitle: 'إعداد العمل دون اتصال',
    offlineDescription: 'تعمل الأدوات الأساسية دون اتصال بعد تخزين التطبيق. ثبّت حزم OCR والنماذج المحلية عند الحاجة فقط.',
    browsePacks: 'استعراض حزم العمل دون اتصال',
  },
  categories: {
    organize: 'تنظيم PDF',
    optimize: 'تحسين PDF',
    toPdf: 'التحويل إلى PDF',
    fromPdf: 'التحويل من PDF',
    edit: 'تحرير PDF',
    security: 'حماية PDF',
    intelligence: 'أدوات PDF الذكية',
  },
  toolLibrary: {
    title: 'كل الأدوات',
    description: 'ابحث أو تصفّح كل أدوات PDF المحلية وعددها ٣٣.',
    toolCount: '{{count}} أداة',
    requiresPack: 'يتطلب حزمة للعمل دون اتصال',
    worksOffline: 'يعمل دون اتصال',
    favorite: 'مفضلة',
    recentlyUsed: 'مستخدمة مؤخرًا',
    acceptedFormats: 'التنسيقات المقبولة',
    outputFormats: 'تنسيقات الناتج',
    multipleFiles: 'يدعم عدة ملفات',
    limitation: 'ملاحظة حول الإمكانات',
  },
  workflow: {
    steps: {
      files: 'الملفات',
      pages: 'الصفحات',
      options: 'الخيارات',
      process: 'المعالجة',
      result: 'النتيجة',
      stepOf: 'الخطوة {{current}} من {{total}}: {{name}}',
    },
    upload: {
      title: 'اختر ملفاتك',
      titlePdf: 'اختر ملفات PDF',
      dropPrompt: 'أفلت الملفات هنا أو اخترها من جهازك',
      dropActive: 'أفلت الملفات لإضافتها',
      mobilePrompt: 'اختر ملفات من هذا الجهاز',
      cameraPrompt: 'استخدم الكاميرا أو اختر صورًا',
      privacyHint: 'تُقرأ الملفات على هذا الجهاز فقط.',
      acceptedTypes: 'المقبول: {{types}}',
      multipleAllowed: 'يمكنك إضافة عدة ملفات.',
      oneFileAllowed: 'اختر ملفًا واحدًا.',
      passwordProtected: 'محمي بكلمة مرور',
      scanning: 'جارٍ فحص {{name}}…',
    },
    fileList: {
      title: 'الملفات المحددة',
      empty: 'لم تُحدّد أي ملفات.',
      addAnother: 'إضافة ملف آخر',
      removeFile: 'إزالة {{name}}',
      replaceFile: 'استبدال {{name}}',
      fileDetails: '{{pages}}، {{size}}',
      moveFileEarlier: 'نقل {{name}} إلى موضع سابق',
      moveFileLater: 'نقل {{name}} إلى موضع لاحق',
      movedFile: 'نُقل {{name}} إلى الموضع {{position}} من {{total}}.',
    },
    pages: {
      title: 'مراجعة الصفحات',
      thumbnail: 'الصفحة {{page}}',
      thumbnailDetails: 'الصفحة {{page}}، {{rotation}}°، {{selection}}',
      selected: 'محددة',
      notSelected: 'غير محددة',
      selectPage: 'تحديد الصفحة {{page}}',
      deselectPage: 'إلغاء تحديد الصفحة {{page}}',
      selectRange: 'تحديد نطاق صفحات',
      rangeHelp: 'استخدم «الكل» أو «فردية» أو «زوجية» أو نطاقات مثل ١–٣، ٥، ٨–١٠.',
      rangePlaceholder: 'الكل أو ١–٣، ٥',
      rotateClockwise: 'تدوير الصفحة {{page}} باتجاه عقارب الساعة',
      rotateCounterclockwise: 'تدوير الصفحة {{page}} عكس اتجاه عقارب الساعة',
      deletePage: 'حذف الصفحة {{page}}',
      duplicatePage: 'تكرار الصفحة {{page}}',
      movePageEarlier: 'نقل الصفحة {{page}} إلى موضع سابق',
      movePageLater: 'نقل الصفحة {{page}} إلى موضع لاحق',
      movedPage: 'نُقلت الصفحة {{page}} إلى الموضع {{position}} من {{total}}.',
      addBlank: 'إضافة صفحة فارغة',
      insertFromFile: 'إدراج صفحات من ملف PDF آخر',
    },
    options: {
      title: 'اختر الخيارات',
      required: 'مطلوب',
      optional: 'اختياري',
      resetAll: 'إعادة ضبط كل الخيارات',
      automaticFilename: 'اترك الحقل فارغًا لإنشاء اسم تلقائي.',
      outputFilenameHelp: 'تُضاف لاحقة الملف تلقائيًا.',
    },
    password: {
      title: 'أدخل كلمة مرور ملف PDF',
      description: 'تُستخدم كلمة المرور في الذاكرة فقط ولا تُحفظ.',
      currentPassword: 'كلمة المرور الحالية',
      openPassword: 'كلمة مرور الفتح',
      permissionsPassword: 'كلمة مرور الأذونات',
      confirmPassword: 'تأكيد كلمة المرور',
      showPassword: 'إظهار كلمة المرور',
      hidePassword: 'إخفاء كلمة المرور',
      neverStored: 'لا تُحفظ كلمات المرور ولا تُرسل.',
    },
    processing: {
      title: 'تجري المعالجة على هذا الجهاز…',
      keepOpen: 'أبقِ علامة التبويب مفتوحة حتى تنتهي المعالجة.',
      preparingFiles: 'جارٍ تحضير الملفات…',
      loadingEngine: 'جارٍ تحميل محرك المعالجة المحلي…',
      processingFile: 'جارٍ معالجة {{name}}…',
      processingFileNumber: 'جارٍ معالجة الملف {{current}} من {{total}}…',
      processingPage: 'تجري معالجة الصفحة {{current}} من {{total}}…',
      finalizing: 'جارٍ إنشاء الناتج…',
      validating: 'جارٍ إعادة فتح الناتج والتحقق منه…',
      cleaning: 'جارٍ مسح بيانات العمل المؤقتة…',
      cancel: 'إلغاء المعالجة',
      cancelConfirmTitle: 'هل تريد إلغاء المعالجة؟',
      cancelConfirmDescription: 'سيُتخلص من الناتج الجزئي وتُمسح بيانات العمل المؤقتة.',
    },
    result: {
      title: 'ملفك جاهز',
      multipleTitle: 'ملفاتك جاهزة',
      validated: 'أُعيد فتح الناتج بنجاح.',
      validationWarning: 'أُنشئ الناتج، لكن تعذّر التحقق منه بالكامل.',
      originalSize: 'الحجم الأصلي',
      resultSize: 'حجم الناتج',
      spaceSaved: 'المساحة الموفرة',
      savedAmount: 'تم توفير {{size}} ({{percent}}٪)',
      noReduction: 'هذا الملف مضغوط بكفاءة بالفعل، وقد لا يكون الناتج أصغر حجمًا.',
      previewResult: 'معاينة الناتج',
      downloadResult: 'تنزيل الناتج',
      downloadNamed: 'تنزيل {{name}}',
      clearTitle: 'هل تريد مسح هذه المهمة؟',
      clearDescription: 'ستُزال الملفات المحددة والمعاينات وروابط العناصر والبيانات المؤقتة الخاصة بهذه المهمة.',
      cleared: 'مُسحت المهمة الحالية وبياناتها المؤقتة.',
    },
    batch: {
      title: 'المعالجة المجمّعة',
      filesQueued: '{{count}} ملفات في قائمة الانتظار',
      completed: 'اكتمل {{complete}} من {{total}}',
      failed: 'فشل {{count}}',
      downloadSuccessful: 'تنزيل النتائج الناجحة',
    },
  },
  offline: {
    connectivity: {
      title: 'حالة الاتصال',
      online: 'متصل',
      offline: 'غير متصل',
      onlineDescription: 'التطبيق متصل. ومع ذلك تبقى الملفات على هذا الجهاز.',
      offlineDescription: 'التطبيق غير متصل. تظل الأدوات والحزم المثبّتة متاحة.',
    },
    readiness: {
      title: 'جاهزية العمل دون اتصال',
      ready: 'جاهز للعمل دون اتصال',
      coreReady: 'الأدوات الأساسية جاهزة للعمل دون اتصال.',
      notReady: 'إعداد العمل دون اتصال غير مكتمل',
      notReadyDescription: 'أكمل تخزين التطبيق قبل قطع الاتصال.',
      checking: 'جارٍ التحقق من جاهزية العمل دون اتصال…',
    },
    installApp: {
      title: 'تثبيت سطر PDF',
      description: 'ثبّت التطبيق للوصول إليه بسرعة أكبر والعمل دون اتصال بموثوقية أفضل.',
      action: 'تثبيت التطبيق',
      installed: 'التطبيق مثبّت',
      unavailable: 'تثبيت التطبيق غير متاح في هذا المتصفح.',
    },
    packs: {
      title: 'حزم العمل دون اتصال',
      description: 'تستخدم ميزات OCR والذكاء الاصطناعي المحلي حزمًا اختيارية من المصدر نفسه. ثبّت كل حزمة مرة واحدة ثم استخدمها دون اتصال.',
      downloadAll: 'تنزيل كل ما يلزم للعمل دون اتصال',
      removeAll: 'إزالة كل الحزم الاختيارية',
      englishOcr: 'حزمة التعرّف الضوئي على النص الإنجليزي',
      arabicOcr: 'حزمة التعرّف الضوئي على النص العربي',
      translation: 'حزمة الترجمة بين الإنجليزية والعربية',
      summarization: 'حزمة التلخيص',
      downloadSize: 'حجم التنزيل',
      installedSize: 'الحجم بعد التثبيت',
      deviceRequirements: 'متطلبات الجهاز',
      supportedFeatures: 'الميزات المدعومة',
      installPack: 'تثبيت {{name}}',
      removePack: 'إزالة {{name}}',
      installProgress: 'جارٍ تثبيت {{name}}: {{percent}}٪',
      installedPack: 'حزمة {{name}} مثبّتة وجاهزة للعمل دون اتصال.',
      missingForTool: 'تحتاج أداة {{tool}} إلى {{pack}}.',
      finishBeforeOffline: 'يجب اكتمال تنزيل هذه الحزمة قبل قطع الاتصال.',
      removalNote: 'لا تؤدي إزالة الحزمة إلى حذف مستنداتك لأن المستندات لا تُخزّن مع حزم النماذج.',
    },
    storage: {
      title: 'التخزين دون اتصال',
      description: 'أدِر ملفات التطبيق والنماذج الاختيارية ومساحة المتصفح المؤقتة.',
      used: 'يستخدم سطر PDF مساحة {{used}}',
      usedOfQuota: 'المستخدم {{used}} من نحو {{quota}} متاح',
      estimating: 'جارٍ حساب مساحة التخزين…',
      unavailable: 'تقديرات مساحة التخزين غير متاحة في هذا المتصفح.',
      persistent: 'التخزين الدائم مفعّل.',
      notPersistent: 'قد يزيل المتصفح الملفات المخزنة عندما تقل المساحة.',
      requestPersistent: 'الاحتفاظ بملفات العمل دون اتصال',
      clearModelsAndTemporary: 'مسح النماذج المخزنة والملفات المؤقتة',
      clearTitle: 'هل تريد مسح النماذج المخزنة والملفات المؤقتة؟',
      clearDescription: 'ستحتاج إلى تنزيل حزم OCR والذكاء الاصطناعي الاختيارية مرة أخرى. لا تتضمن هذه الذاكرة مستندات المستخدم.',
      cleared: 'مُسحت النماذج المخزنة والملفات المؤقتة.',
    },
    update: {
      available: 'يتوفر تحديث',
      description: 'أعد التحميل بعد الانتهاء من المهمة الحالية. تؤدي إعادة التحميل إلى مسح بياناتها المؤقتة.',
      reload: 'إعادة التحميل للتحديث',
      later: 'لاحقًا',
      current: 'سطر PDF محدّث.',
    },
  },
  settings: {
    title: 'الإعدادات',
    description: 'أدِر اللغة وملفات العمل دون اتصال والخصوصية وإمكانية الوصول وتحديثات التطبيق.',
    sections: {
      general: 'عام',
      offlineStorage: 'العمل دون اتصال والتخزين',
      privacyData: 'الخصوصية والبيانات المحلية',
      updates: 'التحديثات',
      accessibility: 'إمكانية الوصول',
      about: 'حول التطبيق والتراخيص',
    },
    general: {
      language: 'اللغة',
      languageDescription: 'يؤدي تغيير اللغة إلى تحديث اتجاه الواجهة فورًا من دون مسح المهمة الحالية.',
    },
    privacy: {
      title: 'الخصوصية والبيانات المحلية',
      filesStayLocal: 'تبقى الملفات على هذا الجهاز',
      filesStayLocalDescription: 'لا تستخدم معالجة المستندات رفع الملفات أو واجهات API أو التحليلات أو المزامنة في الخلفية أو خدمات التحويل البعيدة.',
      recentTools: 'الأدوات المستخدمة مؤخرًا',
      recentToolsDescription: 'تُحفظ معرّفات الأدوات والتوقيتات فقط. لا تُحفظ أسماء الملفات أو تفاصيل المستندات.',
      favorites: 'الأدوات المفضلة',
      favoritesDescription: 'تُحفظ معرّفات الأدوات المفضلة في هذا المتصفح فقط.',
      savedSignatures: 'التوقيعات المحفوظة',
      savedSignaturesDescription: 'لا تُحفظ التوقيعات إلا بعد موافقة صريحة، وتبقى على هذا الجهاز.',
      manageSignatures: 'إدارة التوقيعات المحفوظة',
      clearCurrentDocument: 'مسح المستند الحالي',
      clearAllData: 'مسح كل البيانات المحلية',
      clearAllTitle: 'هل تريد مسح كل بيانات سطر PDF من هذا المتصفح؟',
      clearAllDescription: 'يؤدي ذلك إلى إزالة التفضيلات والمفضلة والسجل والتوقيعات المحفوظة والحزم المخزنة والبيانات المؤقتة. قد تبقى ملفات التطبيق المثبّتة حتى يمسحها المتصفح.',
      clearAllConfirm: 'مسح كل البيانات المحلية',
      clearAllComplete: 'مُسحت كل بيانات سطر PDF المحلية.',
    },
    updates: {
      title: 'تحديثات التطبيق',
      version: 'الإصدار',
      check: 'البحث عن تحديثات',
      checking: 'جارٍ البحث عن تحديثات…',
      lastChecked: 'آخر تحقق {{time}}',
    },
    accessibility: {
      title: 'إمكانية الوصول',
      motion: 'الحركة',
      motionDescription: 'تقليل الرسوم المتحركة والانتقالات غير الضرورية.',
      followSystem: 'اتباع إعداد النظام',
      reducedMotion: 'تقليل الحركة',
      keyboardHelp: 'مساعدة لوحة المفاتيح',
      keyboardHelpDescription: 'توفر كل إجراءات الملفات والصفحات والمحرر بدائل بلوحة المفاتيح عن السحب.',
    },
    about: {
      title: 'حول سطر PDF',
      description: 'مجموعة أدوات PDF ثنائية اللغة تعمل دون اتصال بالكامل داخل المتصفح.',
      privacyModel: 'نموذج الخصوصية',
      capabilityMatrix: 'مصفوفة إمكانات PDF',
      thirdPartyNotices: 'إشعارات الجهات الخارجية',
      sourceLicenses: 'تراخيص المصادر المفتوحة',
      license: 'الترخيص والضمان',
      licenseDescription: 'حقوق النشر © 2026 لمساهمي سطر PDF. برنامج حر بموجب AGPL-3.0-or-later ويُقدّم دون أي ضمان.',
      sourceCode: 'المصدر المقابل',
      sourceCodeDescription: 'احصل على مصدر التطبيق وMuPDF الدقيق ونصوص البناء الخاصة بهذا الإصدار.',
      version: 'الإصدار {{version}}',
    },
  },
  notices: {
    localOnly: 'تجري المعالجة على هذا الجهاز. أبقِ علامة التبويب مفتوحة.',
    officeBestEffort: 'قد تتغيّر تخطيطات {{format}} المعقّدة. راجع الناتج قبل مشاركته.',
    editableApproximation: 'قد لا يطابق الناتج القابل للتحرير التخطيط الأصلي تمامًا.',
    visualSignature: 'تضيف هذه الأداة توقيعًا مرئيًا، وليس توقيعًا رقميًا موثقًا بشهادة.',
    permissionEnforcement: 'قد تتجاهل بعض قارئات PDF قيود الأذونات.',
    permanentRedaction: 'يصبح الحجب نهائيًا بعد تطبيقه. احتفظ بالملف الأصلي.',
    pdfaValidation: 'لن تُعرض المطابقة إلا بعد اجتياز التحقق المحلي.',
    aiFallback: 'يستخدم هذا الجهاز تلخيصًا استخراجيًا لأنه لا يستطيع تشغيل النموذج المحلي الكامل.',
    translatedLayout: 'تخطيط الترجمة تقريبي. راجعه قبل الاستخدام.',
    croppedContent: 'يغيّر الاقتصاص مساحة الصفحة المرئية؛ وقد يبقى المحتوى الموجود خارجها داخل الملف.',
    strongCompression: 'قد يقلل الضغط القوي إمكانية الوصول إلى النص وجودة الصور.',
  },
  errors: {
    title: 'حدث خطأ',
    generic: 'تعذّر إكمال المهمة. لم يتغيّر ملفك الأصلي.',
    unexpected: 'حدث خطأ غير متوقع.',
    tryAgain: 'حاول مجددًا. إذا استمرت المشكلة، فاستخدم ملفات أقل أو أصغر حجمًا.',
    file: {
      missing: 'اختر ملفًا للمتابعة.',
      tooFew: 'أضف {{minimum}} من الملفات على الأقل للمتابعة.',
      tooMany: 'لا تقبل هذه الأداة أكثر من {{maximum}} من الملفات دفعة واحدة.',
      empty: 'الملف {{name}} فارغ.',
      unsupportedType: 'نوع الملف {{name}} غير مدعوم.',
      extensionMismatch: 'لا يتطابق محتوى {{name}} مع لاحقة الملف.',
      invalidPdf: 'الملف {{name}} ليس ملف PDF صالحًا.',
      damagedPdf: 'تعذّر فتح {{name}}. قد يكون تالفًا أو يستخدم ميزة PDF غير مدعومة.',
      encrypted: 'الملف {{name}} محمي بكلمة مرور.',
      tooLarge: 'قد يكون {{name}} كبيرًا على ذاكرة هذا الجهاز.',
      duplicate: 'أُضيف {{name}} من قبل.',
      archiveUnsafe: 'يحتوي {{name}} على عدد كبير جدًا من العناصر المؤرشفة أو عناصر كبيرة على نحو غير معتاد، ولذلك لا يمكن فتحه بأمان.',
    },
    password: {
      required: 'أدخل كلمة المرور للمتابعة.',
      incorrect: 'كلمة المرور غير صحيحة.',
      mismatch: 'كلمتا المرور غير متطابقتين.',
      weak: 'اختر كلمة مرور أطول وأصعب في التوقّع.',
      samePasswords: 'استخدم كلمتي مرور مختلفتين للفتح والأذونات.',
      unsupportedEncryption: 'يستخدم ملف PDF هذا طريقة تشفير لا يستطيع المتصفح فتحها.',
    },
    pages: {
      required: 'حدّد صفحة واحدة على الأقل.',
      invalidRange: 'أدخل نطاق صفحات صالحًا.',
      rangeHelp: 'استخدم نطاقًا مثل ١–٣، ٥، ٨–١٠.',
      outOfRange: 'يتجاوز نطاق الصفحات عدد صفحات المستند.',
      reversedRange: 'يجب أن يبدأ نطاق الصفحات بالصفحة الأسبق.',
      duplicateRange: 'يتضمن هذا التحديد صفحات مكررة.',
      overlappingRanges: 'تتداخل نطاقات الصفحات هذه.',
      removeAll: 'يجب أن تبقى صفحة واحدة على الأقل في ملف PDF.',
      cropOutsidePage: 'يجب أن تبقى مساحة الاقتصاص داخل الصفحة.',
    },
    memory: {
      title: 'الذاكرة غير كافية',
      description: 'قد يكون هذا الملف كبيرًا على ذاكرة جهازك. أغلق علامات تبويب أخرى أو عالج عددًا أقل من الصفحات.',
      storage: 'لا تتوفر مساحة كافية في المتصفح لإكمال هذه المهمة.',
      model: 'لا تتوفر في هذا الجهاز ذاكرة كافية لتشغيل النموذج المحلي المحدد.',
    },
    processing: {
      failed: 'فشلت المعالجة المحلية.',
      workerStopped: 'توقف عامل المعالجة على نحو غير متوقع.',
      canceled: 'أُلغيت المعالجة وتم التخلص من الناتج الجزئي.',
      timeout: 'استغرقت المعالجة وقتًا طويلًا، فأُوقفت لحماية المتصفح.',
      malformedInput: 'يحتوي الملف على بيانات تالفة تعذّرت معالجتها بأمان.',
      outputInvalid: 'أُنشئ الناتج لكنه لم يجتز التحقق، لذلك لن يُعرض على أنه ناتج ناجح.',
      unsupportedFeature: 'يستخدم هذا المستند ميزة لا يدعمها محرك المعالجة المحلي.',
    },
    offline: {
      packRequired: 'تتطلب أداة {{tool}} حزمة {{pack}}.',
      packNotInstalled: 'ثبّت حزمة العمل دون اتصال المطلوبة قبل استخدام هذه الأداة.',
      packDownloadFailed: 'تعذّر تنزيل حزمة العمل دون اتصال.',
      packCorrupt: 'تعذّر التحقق من الحزمة المثبّتة. أزلها وثبّتها مجددًا.',
      coreNotReady: 'هذه الأداة غير جاهزة للعمل دون اتصال. أعد الاتصال وأكمل الإعداد.',
      unexpectedlyOffline: 'انقطع الاتصال قبل اكتمال تنزيل الملف المحلي المطلوب.',
    },
    storage: {
      unavailable: 'مساحة التخزين المحلية للمتصفح غير متاحة.',
      writeFailed: 'تعذّر حفظ البيانات المؤقتة محليًا.',
      clearFailed: 'تعذّر مسح بعض البيانات المحلية.',
      quotaExceeded: 'مساحة المتصفح ممتلئة. أزل الحزم الاختيارية أو امسح الملفات المؤقتة.',
    },
    download: {
      failed: 'تعذّر بدء التنزيل.',
      blocked: 'حظر المتصفح التنزيل. اسمح بالتنزيلات وحاول مجددًا.',
      zipFailed: 'تعذّر إنشاء أرشيف ZIP.',
    },
    ocr: {
      failed: 'فشل التعرّف على النص في الصفحة {{page}}.',
      noText: 'لم يُكتشف نص مقروء.',
      lowConfidence: 'اكتُشف نص بدرجة ثقة منخفضة. راجع الناتج بعناية.',
      languageMissing: 'ثبّت حزمة OCR للغة {{language}} للمتابعة.',
    },
    browser: {
      unsupported: 'لا يدعم هذا المتصفح ميزة تحتاجها هذه الأداة.',
      cameraDenied: 'رُفض الوصول إلى الكاميرا. يمكنك اختيار صور موجودة بدلاً من ذلك.',
      cameraUnavailable: 'لا تتوفر كاميرا متوافقة. يمكنك اختيار صور موجودة بدلاً من ذلك.',
      webGpuUnavailable: 'النموذج المحلي الكامل غير متاح على هذا الجهاز.',
      serviceWorkerUnavailable: 'لا يستطيع هذا المتصفح إتاحة التطبيق دون اتصال بموثوقية.',
    },
  },
  a11y: {
    dialogOpened: 'فُتح مربع الحوار',
    dialogClosed: 'أُغلق مربع الحوار',
    loadingRegion: 'جارٍ تحميل المحتوى',
    progressLabel: 'تقدم {{task}}',
    progressValue: 'اكتمل {{percent}} بالمئة',
    preview: 'معاينة المستند',
    pagePreview: 'معاينة الصفحة {{page}}',
    thumbnailList: 'الصور المصغرة للصفحات',
    fileList: 'قائمة الملفات المحددة',
    optionsPanel: 'خيارات الأداة',
    resultRegion: 'نتيجة المعالجة',
    statusMessage: 'الحالة: {{status}}',
    errorSummary: 'توجد مشكلات تحتاج إلى انتباهك.',
    directionalIcon: 'أيقونة اتجاهية',
    externalTechnicalTerm: '{{term}}، اسم تنسيق تقني',
  },
} as const satisfies TranslationShape<EnglishTranslations>;
